import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, AlertTriangle } from 'lucide-react';
import { Input, Label, FieldHint, Button } from '../ui';
import { theme } from '../../styles/theme';

/**
 * Editor guiado de regras de agendamento (schedulingRules) para conectores que
 * expõem um `schedulingRuleCatalog` (ex.: Feegow).
 *
 * - Um formulário por tipo, montado a partir do catálogo (fonte única do backend).
 * - Injeção de template ao adicionar uma regra do tipo escolhido.
 * - Verificação de integridade em tempo real (campos obrigatórios por tipo).
 * - Preview em linguagem natural do efeito de cada regra.
 * - Tooltips/hints por tipo (i18n).
 *
 * Todos os campos são inputs de texto simples. Quem configura digita os
 * códigos/valores diretamente (IDs de profissional/especialidade/procedimento/
 * convênio, horários HH:MM, dias 0..6, e os valores literais dos enums).
 *
 * Props:
 *   value: Array<rule>            regras atuais (connector.schedulingRules)
 *   onChange: (rules) => void     atualiza o array de regras
 *   catalog: Array<catalogEntry>  connectorType.schedulingRuleCatalog
 */
const WEEKDAYS = [
  { v: 0, key: 'sun' }, { v: 1, key: 'mon' }, { v: 2, key: 'tue' },
  { v: 3, key: 'wed' }, { v: 4, key: 'thu' }, { v: 5, key: 'fri' }, { v: 6, key: 'sat' },
];

function newId(type) {
  return `${type}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Validação leve reutilizável (espelha o joi do backend). Retorna array de
 * mensagens de erro; vazio = válido. Usada pelo save do AgentForm para bloquear
 * o envio de regras incompletas antes do backend (que é autoritativo).
 */
export function findSchedulingRulesErrors(rules) {
  if (!Array.isArray(rules)) return [];
  const errs = [];
  const seen = new Set();
  rules.forEach((rule, i) => {
    const label = `#${i + 1}`;
    if (rule.id) {
      if (seen.has(rule.id)) errs.push(`${label}: id duplicado`);
      seen.add(rule.id);
    }
    if (!rule.type) { errs.push(`${label}: tipo obrigatório`); return; }
    if (rule.type === 'slot_time_restriction') {
      const mode = rule.constraint?.mode;
      if (!mode) errs.push(`${label}: modo obrigatório`);
      if (mode === 'time_window' && (!rule.constraint?.window?.start || !rule.constraint?.window?.end)) {
        errs.push(`${label}: janela incompleta`);
      }
    }
    if (rule.type === 'weekday_restriction' && (!Array.isArray(rule.allowedWeekdays) || rule.allowedWeekdays.length === 0)) {
      errs.push(`${label}: selecione ao menos um dia`);
    }
    if (rule.type === 'lead_time' && rule.minMinutes == null && rule.maxDays == null) {
      errs.push(`${label}: informe antecedência mínima ou máxima`);
    }
    if (rule.type === 'period_quota') {
      if (rule.max == null || rule.max < 0) errs.push(`${label}: máximo obrigatório`);
      if (rule.period?.kind === 'daypart' && !rule.period?.daypart) errs.push(`${label}: selecione manhã/tarde`);
    }
  });
  return errs;
}

// Lê/escreve caminhos aninhados ('constraint.window.start') de forma imutável.
function getPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}
function setPath(obj, path, val) {
  const keys = path.split('.');
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = cur[k] == null ? {} : (Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] });
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = val;
  return clone;
}

const SchedulingRulesEditor = ({ value, onChange, catalog = [] }) => {
  const { t } = useTranslation(['agents', 'common']);
  const rules = Array.isArray(value) ? value : [];

  const tr = (key, fallback, opts) => t(key, { defaultValue: fallback, ...(opts || {}) });

  const catalogByType = useMemo(() => {
    const m = {};
    for (const c of catalog) m[c.type] = c;
    return m;
  }, [catalog]);

  const addRule = (type) => {
    const entry = catalogByType[type];
    if (!entry) return;
    const clone = JSON.parse(JSON.stringify(entry.template || { type }));
    clone.id = newId(type);
    clone.enabled = true;
    onChange([...(rules || []), clone]);
  };

  const removeRule = (idx) => onChange(rules.filter((_, i) => i !== idx));
  const updateRule = (idx, path, val) =>
    onChange(rules.map((r, i) => (i === idx ? setPath(r, path, val) : r)));

  // ── verificação de integridade (espelha o joi do backend, versão leve) ────
  const validateRule = (rule) => {
    const errs = [];
    if (!rule.type) errs.push(tr('schedulingRules.errors.missingType', 'Tipo obrigatório.'));
    if (rule.type === 'slot_time_restriction') {
      const mode = getPath(rule, 'constraint.mode');
      if (!mode) errs.push(tr('schedulingRules.errors.missingMode', 'Selecione o modo.'));
      if (mode === 'time_window') {
        if (!getPath(rule, 'constraint.window.start') || !getPath(rule, 'constraint.window.end')) {
          errs.push(tr('schedulingRules.errors.missingWindow', 'Informe início e fim da janela.'));
        }
      }
    }
    if (rule.type === 'weekday_restriction') {
      if (!Array.isArray(rule.allowedWeekdays) || rule.allowedWeekdays.length === 0) {
        errs.push(tr('schedulingRules.errors.missingWeekdays', 'Selecione ao menos um dia.'));
      }
    }
    if (rule.type === 'lead_time') {
      if (rule.minMinutes == null && rule.maxDays == null) {
        errs.push(tr('schedulingRules.errors.missingLeadTime', 'Informe antecedência mínima ou máxima.'));
      }
    }
    if (rule.type === 'period_quota') {
      if (rule.max == null || rule.max < 0) errs.push(tr('schedulingRules.errors.missingMax', 'Informe o máximo.'));
      if (getPath(rule, 'period.kind') === 'daypart' && !getPath(rule, 'period.daypart')) {
        errs.push(tr('schedulingRules.errors.missingDaypart', 'Selecione manhã ou tarde.'));
      }
    }
    return errs;
  };

  // ── preview em linguagem natural ──────────────────────────────────────────
  const describeMatch = (rule) => {
    const m = rule.match || {};
    const parts = [];
    if (m.procedimentoIds?.length) parts.push(tr('schedulingRules.preview.forProcedures', 'procedimento(s) {{ids}}', { ids: m.procedimentoIds.join(', ') }));
    if (m.especialidadeIds?.length) parts.push(tr('schedulingRules.preview.forSpecialties', 'especialidade(s) {{ids}}', { ids: m.especialidadeIds.join(', ') }));
    if (m.profissionalIds?.length) parts.push(tr('schedulingRules.preview.forProfessionals', 'profissional(is) {{ids}}', { ids: m.profissionalIds.join(', ') }));
    if (m.convenioId != null) parts.push(tr('schedulingRules.preview.forInsurance', 'convênio {{id}}', { id: m.convenioId }));
    if (parts.length === 0) return tr('schedulingRules.preview.forAll', 'para todos os atendimentos');
    return parts.join(', ');
  };

  const describeRule = (rule) => {
    const scope = describeMatch(rule);
    switch (rule.type) {
      case 'slot_time_restriction': {
        const mode = getPath(rule, 'constraint.mode');
        const map = {
          last_slot_of_day: tr('schedulingRules.preview.lastSlot', 'somente no último horário de cada dia'),
          first_slot_of_day: tr('schedulingRules.preview.firstSlot', 'somente no primeiro horário de cada dia'),
          morning: tr('schedulingRules.preview.morning', 'somente pela manhã'),
          afternoon: tr('schedulingRules.preview.afternoon', 'somente à tarde'),
          time_window: tr('schedulingRules.preview.window', 'somente entre {{start}} e {{end}}', {
            start: getPath(rule, 'constraint.window.start') || '--:--',
            end: getPath(rule, 'constraint.window.end') || '--:--',
          }),
        };
        return tr('schedulingRules.preview.timeRestriction', 'Oferecer {{scope}} {{when}}.', { scope, when: map[mode] || '' });
      }
      case 'weekday_restriction': {
        const days = (rule.allowedWeekdays || []).map((v) => tr(`schedulingRules.weekdays.${WEEKDAYS.find((w) => w.v === v)?.key}`, String(v))).join(', ');
        return tr('schedulingRules.preview.weekday', 'Oferecer {{scope}} somente em: {{days}}.', { scope, days });
      }
      case 'lead_time': {
        const bits = [];
        if (rule.minMinutes != null) bits.push(tr('schedulingRules.preview.minLead', 'no mínimo {{min}} minutos de antecedência', { min: rule.minMinutes }));
        if (rule.maxDays != null) bits.push(tr('schedulingRules.preview.maxLead', 'no máximo {{max}} dias de antecedência', { max: rule.maxDays }));
        return tr('schedulingRules.preview.leadTime', 'Agendar {{scope}} com {{bits}}.', { scope, bits: bits.join(' e ') });
      }
      case 'period_quota': {
        const period = getPath(rule, 'period.kind') === 'daypart'
          ? tr(`schedulingRules.preview.${getPath(rule, 'period.daypart')}`, getPath(rule, 'period.daypart'))
          : tr('schedulingRules.preview.perDay', 'por dia');
        const scopeMap = {
          per_professional: tr('schedulingRules.preview.perProfessional', 'por profissional'),
          per_unit: tr('schedulingRules.preview.perUnit', 'por unidade'),
          per_clinic: tr('schedulingRules.preview.perClinic', 'na clínica'),
        };
        return tr('schedulingRules.preview.quota', 'No máximo {{max}} agendamento(s) {{scope}} {{period}} ({{by}}).', {
          max: rule.max, scope, period, by: scopeMap[rule.scope] || rule.scope,
        });
      }
      default:
        return '';
    }
  };

  // ── renderização de campos do catálogo ────────────────────────────────────
  // Todos os campos são inputs de texto simples. Quem configura digita os
  // códigos/valores diretamente. Sem selects, sem chips, sem lookup.
  const renderField = (rule, idx, field) => {
    const val = getPath(rule, field.name);

    // Dias da semana: botões selecionáveis (0=domingo … 6=sábado).
    if (field.kind === 'weekdays') {
      const arr = Array.isArray(val) ? val : [];
      return (
        <PillGroup>
          {WEEKDAYS.map((w) => {
            const on = arr.includes(w.v);
            return (
              <Pill
                key={w.v}
                type="button"
                $on={on}
                onClick={() =>
                  updateRule(
                    idx,
                    field.name,
                    on ? arr.filter((x) => x !== w.v) : [...arr, w.v].sort((a, b) => a - b)
                  )
                }
              >
                {tr(`schedulingRules.weekdays.${w.key}`, w.key)}
              </Pill>
            );
          })}
        </PillGroup>
      );
    }

    // Listas de IDs (profissionais/especialidades/procedimentos): entrada como
    // números separados por vírgula.
    if (field.kind === 'multiselect') {
      const arr = Array.isArray(val) ? val : [];
      return (
        <Input
          value={arr.join(', ')}
          placeholder="ex: 12, 34"
          onChange={(e) => {
            const ids = e.target.value
              .split(',')
              .map((s) => parseInt(s.trim(), 10))
              .filter((n) => !Number.isNaN(n));
            updateRule(idx, field.name, ids);
          }}
        />
      );
    }

    // ID único (ex: convênio) — input de texto numérico.
    if (field.kind === 'select' && field.source) {
      return (
        <Input
          value={val ?? ''}
          placeholder={tr('schedulingRules.enterId', 'ID')}
          onChange={(e) => {
            const raw = e.target.value.trim();
            const n = parseInt(raw, 10);
            updateRule(idx, field.name, raw === '' ? null : (Number.isNaN(n) ? raw : n));
          }}
        />
      );
    }

    // Enum (mode/kind/daypart/scope) — botões selecionáveis (radio/breadcrumb).
    if (field.kind === 'select') {
      return (
        <PillGroup>
          {(field.options || []).map((opt) => (
            <Pill
              key={opt}
              type="button"
              $on={val === opt}
              onClick={() => updateRule(idx, field.name, opt)}
            >
              {tr(`schedulingRules.options.${opt}`, opt)}
            </Pill>
          ))}
        </PillGroup>
      );
    }

    // Horário HH:MM e números — input de texto.
    if (field.kind === 'time') {
      return (
        <Input
          value={val || ''}
          placeholder="HH:MM"
          onChange={(e) => updateRule(idx, field.name, e.target.value.trim())}
        />
      );
    }

    if (field.kind === 'number') {
      return (
        <Input
          value={val ?? ''}
          placeholder="0"
          onChange={(e) => {
            const raw = e.target.value.trim();
            const n = parseInt(raw, 10);
            updateRule(idx, field.name, raw === '' ? null : (Number.isNaN(n) ? raw : n));
          }}
        />
      );
    }

    // fallback: texto simples
    return (
      <Input value={val ?? ''} onChange={(e) => updateRule(idx, field.name, e.target.value)} />
    );
  };


  const shouldShow = (rule, field) => {
    if (!field.showWhen) return true;
    return Object.entries(field.showWhen).every(([p, expected]) => getPath(rule, p) === expected);
  };

  const fieldLabel = (field) => {
    const leaf = field.name.split('.').pop();
    return tr(`schedulingRules.fields.${leaf}`, leaf);
  };

  return (
    <Wrap>
      <HeaderRow>
        <div>
          <Label style={{ margin: 0 }}>{tr('schedulingRules.title', 'Regras de agendamento')}</Label>
          <FieldHint>{tr('schedulingRules.intro', 'Regras aplicadas automaticamente aos horários oferecidos e ao criar agendamentos, sem depender da IA.')}</FieldHint>
        </div>
      </HeaderRow>

      {rules.length === 0 && (
        <Empty>{tr('schedulingRules.empty', 'Nenhuma regra configurada. As disponibilidades seguem o padrão da Feegow.')}</Empty>
      )}

      {rules.map((rule, idx) => {
        const entry = catalogByType[rule.type];
        const errs = validateRule(rule);
        return (
          <RuleCard key={rule.id || idx} $invalid={errs.length > 0}>
            <RuleHead>
              <RuleTitle>{tr(entry?.labelKey, rule.type)}</RuleTitle>
              <RuleActions>
                <ToggleMini
                  $on={rule.enabled !== false}
                  onClick={() => updateRule(idx, 'enabled', rule.enabled === false)}
                  title={rule.enabled !== false ? tr('schedulingRules.enabled', 'Ativa') : tr('schedulingRules.disabled', 'Inativa')}
                >
                  {rule.enabled !== false ? tr('schedulingRules.enabled', 'Ativa') : tr('schedulingRules.disabled', 'Inativa')}
                </ToggleMini>
                <IconBtn onClick={() => removeRule(idx)} title={tr('common:remove', 'Remover')}><Trash2 size={15} /></IconBtn>
              </RuleActions>
            </RuleHead>

            {entry?.helpKey && <RuleHelp>{tr(entry.helpKey, '')}</RuleHelp>}

            <Fields>
              {(entry?.fields || []).filter((f) => shouldShow(rule, f)).map((field) => (
                <FieldRow key={field.name}>
                  <FieldLabel>
                    {fieldLabel(field)}{field.optional ? '' : ' *'}
                  </FieldLabel>
                  {renderField(rule, idx, field)}
                </FieldRow>
              ))}
            </Fields>

            {errs.length > 0 ? (
              <ErrorBox>
                <AlertTriangle size={14} />
                <span>{errs.join(' ')}</span>
              </ErrorBox>
            ) : (
              <Preview>{describeRule(rule)}</Preview>
            )}
          </RuleCard>
        );
      })}

      <AddRow>
        <Label style={{ margin: 0, fontSize: 12 }}>{tr('schedulingRules.addRule', 'Adicionar regra')}</Label>
        <AddButtons>
          {catalog.map((c) => (
            <Button key={c.type} type="button" variant="secondary" size="sm" onClick={() => addRule(c.type)}>
              <Plus size={13} /> {tr(c.labelKey, c.type)}
            </Button>
          ))}
        </AddButtons>
      </AddRow>
    </Wrap>
  );
};

export default SchedulingRulesEditor;

// ── styles ──────────────────────────────────────────────────────────────────
const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[3]};
  padding-top: ${theme.spacing[3]};
  border-top: 1px solid ${theme.colors.border};
`;
const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
const Empty = styled.div`
  font-size: 12px;
  color: ${theme.colors.textMuted};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.surfaceAlt || theme.colors.background};
  border-radius: ${theme.radii?.md || '8px'};
  text-align: center;
`;
const RuleCard = styled.div`
  border: 1px solid ${(p) => (p.$invalid ? theme.colors.warning || '#d97706' : theme.colors.border)};
  border-radius: ${theme.radii?.md || '8px'};
  padding: ${theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  background: ${theme.colors.surface || '#fff'};
`;
const RuleHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const RuleTitle = styled.strong`
  font-size: 13px;
  color: ${theme.colors.text};
`;
const RuleActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;
const RuleHelp = styled.div`
  font-size: 11px;
  color: ${theme.colors.textMuted};
`;
const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;
const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  align-items: center;
  gap: ${theme.spacing[2]};
`;
const FieldLabel = styled.span`
  font-size: 12px;
  color: ${theme.colors.textMuted};
`;
const Preview = styled.div`
  font-size: 12px;
  color: ${theme.colors.text};
  background: ${theme.colors.surfaceAlt || 'rgba(0,0,0,0.03)'};
  border-radius: 6px;
  padding: 8px 10px;
  font-style: italic;
`;
const ErrorBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${theme.colors.warning || '#b45309'};
  background: ${theme.colors.warningBg || 'rgba(217,119,6,0.08)'};
  border-radius: 6px;
  padding: 8px 10px;
`;
const AddRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;
const AddButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;
const PillGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const Pill = styled.button`
  border: 1px solid ${(p) => (p.$on ? theme.colors.primary : theme.colors.border)};
  background: ${(p) => (p.$on ? theme.colors.primary : 'transparent')};
  color: ${(p) => (p.$on ? '#fff' : theme.colors.text)};
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
`;
const ToggleMini = styled.button`
  border: 1px solid ${(p) => (p.$on ? theme.colors.success || '#16a34a' : theme.colors.border)};
  background: transparent;
  color: ${(p) => (p.$on ? theme.colors.success || '#16a34a' : theme.colors.textMuted)};
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11px;
  cursor: pointer;
`;
const IconBtn = styled.button`
  border: none;
  background: transparent;
  color: ${theme.colors.textMuted};
  cursor: pointer;
  display: flex;
  align-items: center;
  &:hover { color: ${theme.colors.danger || '#dc2626'}; }
`;
